import { HOVER_IDLE_SAVE_DATA_VERSION } from "@/features/hoverIdle/persistence/hoverIdleSaveData";
import type { HoverIdleSaveData } from "@/features/hoverIdle/persistence/hoverIdleSaveData";
import { decodeHoverIdleSaveData } from "@/features/hoverIdle/persistence/hoverIdleSaveCodec";
import type { HoverIdleSaveStorage } from "@/features/hoverIdle/persistence/hoverIdleStorage";
import type { EntitlementSnapshot } from "@/features/payment/types";
import { createTimOfflineRewardResult } from "./createTimOfflineReward";
import { canonicalDigest } from "./offlineRewardDigest";
import {
  OFFLINE_REWARD_FORMULA_VERSION,
  type OfflineRewardCommitFailure,
  type OfflineRewardElapsed,
  type OfflineRewardInput,
  type OfflineRewardResult,
} from "./offlineRewardTypes";
import { normalizeOfflineTimeScale } from "./offlineTimeScale";
import { validateTimOfflineReward } from "./validateTimOfflineReward";

export const OFFLINE_REWARD_CAP_MS = 24 * 60 * 60_000;
export const OFFLINE_REWARD_MIN_GAME_ELAPSED_MS = 30_000;
const END_TO_END_HARD_CEILING_MS = 100;

export interface ProductionOfflineRewardTiming {
  readonly inputPreparationMs: number;
  readonly mainThreadTasksMs: readonly number[];
  readonly mainThreadSingleTaskMs: number;
  readonly mainThreadTotalBlockingMs: number;
  readonly commitMs: number;
  readonly endToEndMs: number;
  readonly hardCeilingPassed: boolean;
}

export type ProductionOfflineRewardLifecycleEvent =
  | "offline-reward-elapsed-determined"
  | "offline-reward-calculation-start"
  | "offline-reward-calculation-complete"
  | "offline-reward-validation-complete"
  | "atomic-commit-start"
  | "atomic-commit-complete";

export type ProductionOfflineRewardOutcome =
  | {
      readonly kind: "not-needed";
      readonly elapsed: OfflineRewardElapsed;
      readonly timing: ProductionOfflineRewardTiming;
    }
  | {
      readonly kind: "committed";
      readonly input: OfflineRewardInput;
      readonly elapsed: OfflineRewardElapsed;
      readonly result: OfflineRewardResult;
      readonly saveData: HoverIdleSaveData;
      readonly recoveredFromReceipt: boolean;
      readonly timing: ProductionOfflineRewardTiming;
    }
  | {
      readonly kind: "commit-unavailable";
      readonly message: string;
      readonly elapsed: OfflineRewardElapsed;
      readonly timing: ProductionOfflineRewardTiming;
    }
  | {
      readonly kind: "invalid-result";
      readonly message: string;
      readonly elapsed: OfflineRewardElapsed;
      readonly timing: ProductionOfflineRewardTiming;
    }
  | {
      readonly kind: "commit-rejected";
      readonly reason: OfflineRewardCommitFailure;
      readonly elapsed: OfflineRewardElapsed;
      readonly timing: ProductionOfflineRewardTiming;
    };

export function createProductionOfflineRewardInput(options: {
  readonly startSave: HoverIdleSaveData;
  readonly committedAt: number;
  readonly ownerEntitlementSnapshot: EntitlementSnapshot;
  readonly adminTimeScale?: number;
  readonly systemTimeScale?: number;
}): OfflineRewardInput {
  return prepareProductionOfflineReward(options).input;
}

export async function calculateAndCommitProductionOfflineReward(options: {
  readonly storage: HoverIdleSaveStorage;
  readonly input: OfflineRewardInput;
  readonly elapsed: OfflineRewardElapsed;
  readonly inputPreparationMs?: number;
  readonly inputPreparationTaskMs?: readonly number[];
  readonly onLifecycleEvent?: (
    event: ProductionOfflineRewardLifecycleEvent,
  ) => void;
}): Promise<ProductionOfflineRewardOutcome> {
  const startedAt = performance.now();
  const inputPreparationMs = Math.max(0, options.inputPreparationMs ?? 0);
  const mainThreadTasksMs = [
    ...(options.inputPreparationTaskMs ??
      (inputPreparationMs > 0 ? [inputPreparationMs] : [])),
  ];
  let mainThreadTotalBlockingMs = mainThreadTasksMs.reduce(
    (total, taskMs) => total + taskMs,
    0,
  );
  let mainThreadSingleTaskMs = Math.max(0, ...mainThreadTasksMs);

  if (
    options.input.startSave.version !== HOVER_IDLE_SAVE_DATA_VERSION ||
    options.input.gameElapsedMs < OFFLINE_REWARD_MIN_GAME_ELAPSED_MS
  ) {
    return {
      kind: "not-needed",
      elapsed: options.elapsed,
      timing: createTiming(
        startedAt,
        mainThreadSingleTaskMs,
        mainThreadTotalBlockingMs,
        0,
        inputPreparationMs,
        mainThreadTasksMs,
      ),
    };
  }
  if (options.storage.commitOfflineReward === undefined) {
    return {
      kind: "commit-unavailable",
      message: "atomic offline reward commit is unavailable",
      elapsed: options.elapsed,
      timing: createTiming(
        startedAt,
        mainThreadSingleTaskMs,
        mainThreadTotalBlockingMs,
        0,
        inputPreparationMs,
        mainThreadTasksMs,
      ),
    };
  }

  emitLifecycleEvent(
    options.onLifecycleEvent,
    "offline-reward-calculation-start",
  );
  let taskStartedAt = performance.now();
  const result = createTimOfflineRewardResult(options.input);
  recordTask(performance.now() - taskStartedAt);
  emitLifecycleEvent(
    options.onLifecycleEvent,
    "offline-reward-calculation-complete",
  );

  taskStartedAt = performance.now();
  const validation = validateTimOfflineReward(options.input, result);
  recordTask(performance.now() - taskStartedAt);
  emitLifecycleEvent(
    options.onLifecycleEvent,
    "offline-reward-validation-complete",
  );
  if (!validation.ok) {
    return {
      kind: "invalid-result",
      message: validation.errors.map((error) => error.message).join("; "),
      elapsed: options.elapsed,
      timing: createTiming(
        startedAt,
        mainThreadSingleTaskMs,
        mainThreadTotalBlockingMs,
        0,
        inputPreparationMs,
        mainThreadTasksMs,
      ),
    };
  }

  const commitStartedAt = performance.now();
  emitLifecycleEvent(options.onLifecycleEvent, "atomic-commit-start");
  const committed = await options.storage.commitOfflineReward({
    input: options.input,
    result,
  });
  emitLifecycleEvent(options.onLifecycleEvent, "atomic-commit-complete");
  const commitMs = performance.now() - commitStartedAt;
  const timing = createTiming(
    startedAt,
    mainThreadSingleTaskMs,
    mainThreadTotalBlockingMs,
    commitMs,
    inputPreparationMs,
    mainThreadTasksMs,
  );
  if (!committed.ok) {
    const recoveredSave = await recoverCommittedSave(options.storage, result);
    if (recoveredSave !== null) {
      return {
        kind: "committed",
        input: options.input,
        elapsed: options.elapsed,
        result,
        saveData: recoveredSave,
        recoveredFromReceipt: true,
        timing,
      };
    }
    return {
      kind: "commit-rejected",
      reason: committed.reason,
      elapsed: options.elapsed,
      timing,
    };
  }
  return {
    kind: "committed",
    input: options.input,
    elapsed: options.elapsed,
    result,
    saveData: committed.saveData,
    recoveredFromReceipt: false,
    timing,
  };

  function recordTask(taskMs: number): void {
    mainThreadTasksMs.push(taskMs);
    mainThreadTotalBlockingMs += taskMs;
    mainThreadSingleTaskMs = Math.max(mainThreadSingleTaskMs, taskMs);
  }
}

export async function calculateAndCommitProductionOfflineRewardSave(options: {
  readonly storage: HoverIdleSaveStorage;
  readonly startSave: HoverIdleSaveData;
  readonly committedAt: number;
  readonly ownerEntitlementSnapshot: EntitlementSnapshot;
  readonly adminTimeScale?: number;
  readonly systemTimeScale?: number;
  readonly onLifecycleEvent?: (
    event: ProductionOfflineRewardLifecycleEvent,
  ) => void;
}): Promise<ProductionOfflineRewardOutcome> {
  const preparationStartedAt = performance.now();
  const taskStartedAt = performance.now();
  const prepared = prepareProductionOfflineReward(options);
  const inputPreparationTaskMs = [performance.now() - taskStartedAt];
  emitLifecycleEvent(
    options.onLifecycleEvent,
    "offline-reward-elapsed-determined",
  );
  return calculateAndCommitProductionOfflineReward({
    storage: options.storage,
    ...prepared,
    inputPreparationMs: performance.now() - preparationStartedAt,
    inputPreparationTaskMs,
    onLifecycleEvent: options.onLifecycleEvent,
  });
}

function prepareProductionOfflineReward(options: {
  readonly startSave: HoverIdleSaveData;
  readonly committedAt: number;
  readonly ownerEntitlementSnapshot: EntitlementSnapshot;
  readonly adminTimeScale?: number;
  readonly systemTimeScale?: number;
}): {
  readonly input: OfflineRewardInput;
  readonly elapsed: OfflineRewardElapsed;
} {
  const adminTimeScale = normalizeOfflineTimeScale(options.adminTimeScale ?? 1);
  const systemTimeScale = normalizeOfflineTimeScale(
    options.systemTimeScale ?? 1,
  );
  const offlineRewardMultiplier = normalizeOfflineTimeScale(
    options.ownerEntitlementSnapshot.offlineProgressMultiplier,
  );
  const previousAt = options.startSave.offlineReward.baselineAt;
  const realElapsedMs = Math.max(0, options.committedAt - previousAt);
  const uncappedGameElapsedMs =
    realElapsedMs * adminTimeScale * systemTimeScale * offlineRewardMultiplier;
  const rewardElapsedMs = Math.min(
    uncappedGameElapsedMs,
    OFFLINE_REWARD_CAP_MS,
  );
  const elapsed = {
    realElapsedMs,
    uncappedGameElapsedMs,
    rewardElapsedMs,
    wasCapped: uncappedGameElapsedMs > rewardElapsedMs,
  };
  return {
    elapsed,
    input: {
      startSave: options.startSave,
      baseline: {
        previousAt,
        committedAt: options.committedAt,
      },
      revision: options.startSave.offlineReward.revision,
      inputDigest: canonicalDigest(options.startSave),
      gameElapsedMs: rewardElapsedMs,
      wasCapped: elapsed.wasCapped,
      rewardFormulaVersion: OFFLINE_REWARD_FORMULA_VERSION,
    },
  };
}

async function recoverCommittedSave(
  storage: HoverIdleSaveStorage,
  result: OfflineRewardResult,
): Promise<HoverIdleSaveData | null> {
  let raw: unknown | null;
  try {
    raw = await storage.loadLatest();
  } catch {
    return null;
  }
  if (raw === null) return null;
  const decoded = decodeHoverIdleSaveData(raw);
  if (!decoded.ok) return null;
  const metadata = decoded.data.offlineReward;
  return metadata.appliedRewardIds.includes(result.rewardId) &&
    metadata.lastReceipt?.rewardId === result.rewardId &&
    metadata.lastReceipt.resultDigest === result.resultDigest
    ? decoded.data
    : null;
}

function emitLifecycleEvent(
  listener:
    | ((event: ProductionOfflineRewardLifecycleEvent) => void)
    | undefined,
  event: ProductionOfflineRewardLifecycleEvent,
): void {
  try {
    listener?.(event);
  } catch {
    // Performance observers are non-authoritative and cannot abort a commit.
  }
}

function createTiming(
  startedAt: number,
  mainThreadSingleTaskMs: number,
  mainThreadTotalBlockingMs: number,
  commitMs: number,
  elapsedBeforeRewardMs: number,
  mainThreadTasksMs: readonly number[],
): ProductionOfflineRewardTiming {
  const endToEndMs = elapsedBeforeRewardMs + (performance.now() - startedAt);
  return {
    inputPreparationMs: elapsedBeforeRewardMs,
    mainThreadTasksMs: [...mainThreadTasksMs],
    mainThreadSingleTaskMs,
    mainThreadTotalBlockingMs,
    commitMs,
    endToEndMs,
    hardCeilingPassed: endToEndMs <= END_TO_END_HARD_CEILING_MS,
  };
}
